import Phaser from 'phaser';
import { kitMeta, kitTextureKey, type NineSliceMargins } from './manifest';

/**
 * Create a Phaser NineSlice from a kit asset key.
 * Falls back to a plain Image (nearest) if margins are missing or texture absent.
 * Origin defaults to top-left (0,0) for layout math matching graphics draws.
 */
export function addKitNineSlice(
  scene: Phaser.Scene,
  assetKey: string,
  x: number,
  y: number,
  width: number,
  height: number,
  marginsOverride?: Partial<NineSliceMargins>,
): Phaser.GameObjects.NineSlice | Phaser.GameObjects.Image {
  const meta = kitMeta(assetKey);
  const tex = kitTextureKey(assetKey);
  if (!scene.textures.exists(tex)) {
    const img = scene.add.image(x, y, 'ui-slot-empty').setOrigin(0, 0);
    img.setDisplaySize(width, height);
    return img;
  }

  const base = meta?.nineSlice;
  const left = marginsOverride?.left ?? base?.left ?? 8;
  const right = marginsOverride?.right ?? base?.right ?? 8;
  const top = marginsOverride?.top ?? base?.top ?? 8;
  const bottom = marginsOverride?.bottom ?? base?.bottom ?? 8;

  const frame = scene.textures.getFrame(tex);
  const srcW = frame.width;
  const srcH = frame.height;

  // Phaser requires width >= left+right and height >= top+bottom.
  const minW = left + right;
  const minH = top + bottom;
  const w = Math.max(width, minW);
  const h = Math.max(height, minH);

  if (!base || srcW < minW || srcH < minH) {
    const img = scene.add.image(x, y, tex).setOrigin(0, 0);
    img.setDisplaySize(w, h);
    return img;
  }

  const ns = scene.add.nineslice(x, y, tex, undefined, w, h, left, right, top, bottom);
  ns.setOrigin(0, 0);
  return ns;
}

/** Nearest-neighbor image at native or integer-scaled size (no smoothing). */
export function addKitImage(
  scene: Phaser.Scene,
  assetKey: string,
  x: number,
  y: number,
  options: { originX?: number; originY?: number; scale?: number } = {},
): Phaser.GameObjects.Image {
  const tex = kitTextureKey(assetKey);
  const key = scene.textures.exists(tex) ? tex : 'ui-slot-empty';
  const img = scene.add
    .image(x, y, key)
    .setOrigin(options.originX ?? 0.5, options.originY ?? 0.5);
  if (options.scale != null) {
    img.setScale(options.scale);
  }
  return img;
}
